import { OwcModalDialog, OwcButton, OwcTypography, OwcInput, OwcCheckbox,
         OwcComponentHeader, OwcComponentFooter, OwcSwitch} from '@one/react';
import DeviceHistory from './DeviceHistory.js';
import DeviceDataSources from './DeviceDataSources.js';
import DeviceAccounts from './DeviceAccounts.js';
import DelayedTooltip from '../general/DelayedTooltip.js';
import DeviceAlias from './DeviceAlias.js';
import React from 'react';
import '../../styles.scss';

import {configData} from "../../config.js";
import {formatDate, fetchSigned, isRecentMessage, convertToDate} from "../../shared/Utilities.js"
import SimpleDateInput from '../general/SimpleDateInput.js';

/**
 * Renders the device details screen
 *
 * @copyright Roche 2023
 * @author Nick Draper
 */
class DeviceDetails extends React.Component {
  /**
   * Constructor 
   * 
   * @param props The properties passed
   */
  constructor(props) {
    super(props);
    this.state = {
      deviceId: props.deviceId,
      deviceIdentityLoaded: false,
      deviceIdentity: {},
      deviceJustExcluded: false,
      originalDeviceExcluded: null,
      originalComment: null,
      submissionState: "",
      visible: false,
      aggregatedView: true,
    };
  }

  /** Runs whenever the properties of the control are changed
   * @param prevProps The previous properties dictionary
   * @param prevState The previous state dictionary
   */
  componentDidUpdate(prevProps, prevState) {
    const statechange = {};
    if (prevProps.deviceId !== this.props.deviceId) {
      if (this.props.deviceId !== null) {
        statechange.deviceId = this.props.deviceId;
      }

    }
    if (this.props.showNow !== null && prevProps.showNow !== this.props.showNow && isRecentMessage(this.props.showNow)) {
      statechange.visible = true;
    }
    if (Object.keys(statechange).length > 0) {
      this.setState(statechange,  () => this.loadDeviceIdentity(this.props.deviceId));
    }
    
  }

  /**
   * Loads the identity data to display for a device
   * @param {*} deviceId the device_id to load
   */
  loadDeviceIdentity(deviceId) {
    console.log("loading device identity for ", deviceId);
    this.setState({ deviceIdentityLoaded: false, aggregatedView: true});

    fetchSigned(configData.DEVICE_DETAILS_API_URL + "identity/" + deviceId + "/")
    .then(res => res.json())
    .then(
      (result) => {
          if (result[0].comment === null) {
            result[0].comment = "";
          }
          if(result[0].doNotInvestigateBefore !== null){
            result[0].doNotInvestigateBefore = convertToDate(result[0].doNotInvestigateBefore);
          }
          this.setState({ deviceIdentity: result[0],  
                          originalDeviceNeedsInvestigation:  result[0].needsInvestigation,
                          originalDeviceDoNotInvestigateBefore:  result[0].doNotInvestigateBefore,
                          originalDeviceExcluded:  result[0].excluded,
                          originalDeviceComment:  result[0].comment === null? "":result[0].comment,
                          deviceIdentityLoaded: true,
                          submissionState: "",
                          deviceJustExcluded: false,
                          aggregatedView: true});
      },
      // Note: it's important to handle errors here
      // instead of a catch() block so that we don't swallow
      // exceptions from actual bugs in components.
      (error) => {
        this.setState({
          error
        });
      }
    )
  }

  /**
   * Updates the device identitiy record in the database with the comment and exclusion status
   */
  updateDeviceIdentity() {
    const identity = {...this.state.deviceIdentity};
    console.log('Updating device_identity');

    if (identity !== undefined && identity.deviceId  !== undefined)
    {
      if (identity.comment !== null &&
          identity.comment !== undefined &&
          identity.comment.trim() === "") {
        identity.comment = null;
      }
      if (identity.doNotInvestigateBefore !== null) {
        identity.doNotInvestigateBefore = formatDate(identity.doNotInvestigateBefore, 'yyyy-MM-dd');
      }
      this.setState({submissionState: "Saving ..."});

      const submitForm = () => {
        const url = configData.DEVICE_DETAILS_API_URL + "identity/" + identity.deviceId + "/";
        const method = 'PUT';
        return fetchSigned(url, {
          method: method,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(identity)
        })
      };

      submitForm().then((response) => {
        response.json().then((json) => {
          console.log(`API Response: ${JSON.stringify(json)}`);
          if (response.status === 200) // inserted successfully
          {
            console.log(`Update to device_identity saved successfully`);
            this.props.onExclusionChange(identity.deviceId, identity.excluded, identity.comment);
            this.props.onInvestigationChange(identity.deviceId, identity.needsInvestigation, identity.comment);
            this.setState({originalDeviceExcluded:identity.excluded,
                           originalDeviceComment: identity.comment,
                           originalDeviceNeedsInvestigation: identity.needsInvestigation,
                           originalDeviceDoNotInvestigateBefore: identity.doNotInvestigateBefore,
                           submissionState: "Saved",
                           deviceJustExcluded: false})
          }
        });
      }).catch((error) => {
        console.error(`Error when saving update to device_identity ${error}`);
      });
    }
  }

  /**
   * Handles click events on the customer agreement
   * Passthrough method to this.props.onAgreementClick
   */
  handleAgreementClick(customerAgreementId) {
    console.log(`agreement onClick ${customerAgreementId} passed on by DeviceDetails`)
    this.props.onAgreementClick(customerAgreementId);
  }

  getSubmissionState(newIdentity){
    let submissionMessage = "";
    if (newIdentity.excluded !== this.state.originalDeviceExcluded 
      || newIdentity.comment !== this.state.originalDeviceComment
      || newIdentity.needsInvestigation !== this.state.originalDeviceNeedsInvestigation
      || formatDate(newIdentity.doNotInvestigateBefore) !== formatDate(this.state.originalDeviceDoNotInvestigateBefore)) {
          submissionMessage = "Unsaved Changes";
      }
    return submissionMessage;
  }

  /**
   * Render the section displaying the device identity
   */
  renderIdentitySection() {
    const identity =this.state.deviceIdentity;
    const exclusionBoxStyle = {display:"flex", flexDirection:"column", flexGrow:"2"};
    if (this.state.deviceIdentityLoaded === true) {
      if (identity.excluded===true) {
        exclusionBoxStyle.backgroundColor=configData.COLOUR_EXCLUDED;
      } else if (identity.needsInvestigation===true) {
        exclusionBoxStyle.backgroundColor=configData.COLOUR_INVESTIGATE;
      }
    }

    return (
      <div style={{marginLeft:"1em", display:"flex", flexDirection:"row", justifyContent:"space-between", width:"calc(98% - 1em)"}}>
        <div style={{ display:"flex", flexDirection:"column", marginRight:"1em", marginTop:"1.3em"}}>
          <div style={{display:"flex", flexDirection:"row"}}>
            <div style={{ display:"flex", flexDirection:"column", marginRight:"1em"}}>
              <OwcTypography style={{ "fontWeight": "bold" }}>Ref System Type:</OwcTypography>
              <OwcTypography style={{ "fontWeight": "bold" }}>Serial No:</OwcTypography>
              <OwcTypography style={{ "fontWeight": "bold" }}>Last Upload Date:</OwcTypography>
            </div>
            <div style={{ display:"flex", flexDirection:"column"}}>
              <OwcTypography>{this.state.deviceIdentityLoaded === false? "" : identity.referenceSystemType + (identity.referenceSystemTypeIsActive===false?configData.REFDATA_DEPRECATED:"")}</OwcTypography>
              <OwcTypography>{this.state.deviceIdentityLoaded === false? "" : identity.serialNo}</OwcTypography>
              <OwcTypography>{this.state.deviceIdentityLoaded === false? "" : formatDate(identity.lastUploadDate)}</OwcTypography>
            </div>
          </div>
            <div style={{display:"flex",  flexDirection:"row", alignItems:"center",  justifyContent: "left"}}>
              <OwcTypography style={{ "fontWeight": "bold", marginLeft: "0em", marginRight: "1em"}}>LifeCycle View:</OwcTypography>
              Aggregated
              <OwcSwitch key="LifecycleViewSwitch"
              checked={!this.state.aggregatedView}
              onValueChange={(ev) => {if (ev.detail === this.state.aggregatedView) {
                                        this.setState({aggregatedView: !ev.detail})
                                      }
                                    }
                            }
              />
              Individual Sources
          </div>
        </div>
        <div style={{flexGrow:"2", marginTop:"-0.3em", marginLeft:"1em"}}>
          <DeviceAlias deviceId={this.props.deviceId} forceUpdate={this.props.showNow} />
        </div>
        <div style={exclusionBoxStyle}>
          <div style={{display:"flex",  flexDirection:"row", alignItems:"center", justifyContent:"space-between"}}>
            <OwcCheckbox
                key={"ExcludedCheckBox" }
                style={{"fontWeight": "bold"}}
                checked={this.state.deviceIdentityLoaded === false? false : identity.excluded}
                disabled={this.state.deviceIdentityLoaded === false}
                onValueChange={(ev) => {
                  if ((ev.type === "inputChange") && (ev.detail === null)) {
                    // don't update anything - prevent null entries
                    return;
                  }
                  if (String(ev.detail) !== String(this.state.deviceIdentity.excluded))
                  {
                    // set the value
                    let newIdentity = {...this.state.deviceIdentity};
                    newIdentity.excluded = ev.detail;
                    const excludedJustSet = (this.state.deviceIdentity.excluded === false && newIdentity.excluded === true);
                    if (newIdentity.excluded === true && newIdentity.needsInvestigation === true)
                    {
                      newIdentity.needsInvestigation = false;
                      if ((newIdentity.doNotInvestigateBefore === null) || 
                        ((newIdentity.doNotInvestigateBefore <= new Date()))) {
                      newIdentity.doNotInvestigateBefore = new Date();
                      newIdentity.doNotInvestigateBefore.setDate(newIdentity.doNotInvestigateBefore.getDate() + 30);
                      }
                    }
                    const submissionState = this.getSubmissionState(newIdentity);
                    this.setState({deviceIdentity: newIdentity,
                                  deviceJustExcluded: excludedJustSet,
                                  submissionState:submissionState});
                  }
                }} >
              <OwcTypography style={{ "fontWeight": "bold" }}>Excluded from mapping to agreements</OwcTypography>
            </OwcCheckbox>
            <OwcButton style={{ width: "fit-content", marginRight: "0.2em" }}
                onclick={()=>this.updateDeviceIdentity()} 
                disabled={this.state.submissionState !== "Unsaved Changes" ? true : false}>
              {this.state.submissionState === "Saving ..." ||  this.state.submissionState === "Saved"? this.state.submissionState : "Save Changes"}
            </OwcButton>
          </div>
          <div style={{display:"flex",  flexDirection:"row", alignItems:"center", marginTop: "-0.2em", padding:"5px 0 5px 0"}}>
            <OwcTypography style={{ "fontWeight": "bold", marginLeft: "1em", marginRight: "1em"}}>Comment:</OwcTypography>
            <OwcInput
              key={"DeviceComments"}
              value={this.state.deviceIdentityLoaded === false? "" : identity.comment}
              disabled={this.state.deviceIdentityLoaded === false}
              label="Comment"
              size="small"
              style={{width:"100%", marginRight: "0.2em"}}
              onBlur={(ev) => {
                if (ev.target.value !== null) {
                  const newComment =  ev.target.value.trim();
                  if (newComment !== this.state.deviceIdentity.comment) {
                    let newIdentity = {...this.state.deviceIdentity};
                    newIdentity.comment = newComment;
                    const submissionState = this.getSubmissionState(newIdentity);
                    this.setState({deviceIdentity: newIdentity,
                                  submissionState: submissionState});
                  }
                }
              }}
            />
          </div>
          <div style={{display:"flex",  flexDirection:"row", alignItems:"center", justifyContent:"space-between"}}>
            <OwcCheckbox
                key={"NeedsInvestigationCheckBox" }
                style={{"fontWeight": "bold"}}
                checked={this.state.deviceIdentityLoaded === false? false : identity.needsInvestigation}
                disabled={this.state.deviceIdentityLoaded === false}
                onValueChange={(ev) => {
                  if ((ev.type === "inputChange") && (ev.detail === null)) {
                    // don't update anything - prevent null entries
                    return;
                  }
                  if (String(ev.detail) !== String(this.state.deviceIdentity.needsInvestigation))
                  {
                    // set the value
                    let newIdentity = {...this.state.deviceIdentity};
                    newIdentity.needsInvestigation = ev.detail;
                    if (newIdentity.needsInvestigation === false)
                    {
                      if ((newIdentity.doNotInvestigateBefore === null) || 
                          ((newIdentity.doNotInvestigateBefore <= new Date()))) {
                        newIdentity.doNotInvestigateBefore = new Date();
                        newIdentity.doNotInvestigateBefore.setDate(newIdentity.doNotInvestigateBefore.getDate() + 30);
                      }
                    }
                    const submissionState = this.getSubmissionState(newIdentity);
                    this.setState({deviceIdentity: newIdentity,
                                  submissionState:submissionState});
                  }
                }} >
              <OwcTypography style={{ "fontWeight": "bold" }}>History needs investigation</OwcTypography>
            </OwcCheckbox>
            <SimpleDateInput name="doNotInvestigateBefore" label="Do not investigate before" 
              style={{ marginRight: "0.25em" }}
              value={identity.doNotInvestigateBefore}
              onValueChange={(ev) => {
                    if (ev !== null) {
                      const newDate =  convertToDate(ev);
                      if (newDate !== identity.doNotInvestigateBefore) {
                        let newIdentity = {...this.state.deviceIdentity};
                        newIdentity.doNotInvestigateBefore = newDate;
                        const submissionState = this.getSubmissionState(newIdentity);
                        this.setState({deviceIdentity: newIdentity,
                                      submissionState:submissionState});
                      }
                    }
                    }}
              showExampleDate={false}>
            </SimpleDateInput>
          </div>

        </div>
      </div>
    );
  }
 
  /**
   * Renders the controls
   * @returns The JSX of the controls
   */
  render() {
    const modalStyle = { maxWidth: '100%' };
    return (
      <OwcModalDialog 
          visible={this.state.visible}
          size="large"
          cardProps={{ style: modalStyle }}
          disableBackdropClick={this.props.disableBackdropClick} 
          disableEscapeKeydown={this.props.disableEscapeKeydown} 
          disableScrollLock={this.props.disableScrollLock} 
          onVisibleChange={(ev)=>{
              if (ev.detail === false) 
                {this.setState({visible: false})};
            }}>
        <div style={{display:"flex", alignItems: "stretch", flexDirection:"column"}}>
          {this.renderIdentitySection()}
          {this.state.aggregatedView === true?
            <DeviceHistory key="aggregatedHistoryView" deviceId={this.state.deviceId} forceUpdate={this.props.showNow} />
          :
            <DeviceDataSources key="individualSourceHistoryView" deviceId={this.state.deviceId} forceUpdate={this.props.showNow} />
          }
          <br/>
          <DeviceAccounts deviceId={this.props.deviceId}
            onAgreementClick={(customerAgreementId)=>this.handleAgreementClick(customerAgreementId)}
            disableNavigation={this.props.disableNavigation}
            displayExcludedWarning={this.state.deviceJustExcluded && this.props.submissionState === "Unsaved Changes"}
            forceUpdate={this.props.showNow} />
        </div>

        <OwcComponentHeader slot="header">
          <OwcTypography style={{lineHeight:"0.2em"}} variant="title6">Device Details</OwcTypography>
        </OwcComponentHeader>
      
        <OwcComponentFooter slot="footer" style={{ display: 'flex', justifyContent: 'flex-end', padding:'30px 10px 10px 10px'}}>
          {this.props.closeButtonText
          ?(<div>
            <OwcButton style={{ marginLeft: "2em" }} 
                id={"DeviceDetailsCloseBtn"} 
                flat
                onClick={(ev) => {this.setState({visible: false})}}>
              {this.props.closeButtonText}
            </OwcButton>
            {
              this.props.closeButtonTooltip
              ? <DelayedTooltip 
                    key={"DeviceDetailsCloseBtnTooltip"} 
                    anchor={"DeviceDetailsCloseBtn"}
                    placement="right"
                    offset={[-15,-200]}>
                  {this.props.closeButtonTooltip}
                </DelayedTooltip>
              : ""
            }
            </div>)
          : ""
          }
        </OwcComponentFooter>
      </OwcModalDialog>
    );

  }
}

DeviceDetails.defaultProps = {
  deviceId: null,
  closeButtonText: "Close",
  closeButtonTooltip: "Close this overlay and return to the screen below",
  visible: true,
  disableBackdropClick: false,
  disableEscapeKeydown: false,
  disableScrollLock: false,
  disableNavigation: false,
  onAgreementClick:() => {},
  onExclusionChange:() => {},
  onInvestigationChange:() => {},
}

export default DeviceDetails
